import { pageQueryParams } from '@/components/search/pageQueryParams'
import { SortableHeader } from '@/components/search/SortableHeader'
import './common.css'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { addServerErrors } from '@/components/forms/common/FormBase'
import { useForm } from 'react-hook-form'
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable'
import { useModal } from '@/components/modal/Modal'
import { formatDate } from '@/common/time'
import { isDesktopScreen, isLargeScreen, useResponsiveChange } from '@/common/device'
import { BackButton } from '@/components/layout/NavBar'
import { CommonUsernameInput, UsernameInput } from '@/components/forms/common/user/input'
import { Spinner } from '@/components/spinner'
import { UCIDInput } from '@/components/forms/common/cards/input'
import { CardFormData, ManagerRemoveCardPopup, SimpleCard } from './common'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Menu, MenuPopupBody } from '@/popups/menupopup/menu'
import { useAppContext } from '@/context/app'
import { extendCommunalCardsMenuPopup } from '@/env/communal/cards/menu'

export interface ManagerEditCardPopupProps {
  services: Services
  session: CurrentSession
  onClose: () => void
  ucid: string | undefined
}

export interface ManagerInsertCardPopupProps {
  services: Services
  session: CurrentSession
  username: string
  onClose: () => void
}

export const ManagerEditCardPopup = ({ services, session, onClose, ucid }: ManagerEditCardPopupProps) => {
  const form = useForm<CardFormData>()
  const [data, setData] = useState<CardFormData>()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!ucid) return
    setData(undefined)
    services.session.fetchWithToken(`/api/communal/cards/byid/${ucid}`).then(r => r.json()).then((data) => {
      setData(data)
      form.reset(data)
    })
  }, [ucid])

  const handle = (data: CardFormData): Promise<void> => {
    if (!data.username) {
      console.error('username is required')
      return Promise.reject()
    }
    if (!form) return Promise.reject()
    return services.session.fetchWithToken(`/api/communal/cards/byid/${ucid}/update`, {
      method: 'PUT',
      body: JSON.stringify(form.getValues())
    }).then(async (r) => {
      if (r.status !== 200) {
        setError('Write data error')
        const errors = await r.json()
        addServerErrors(form, session.translates, errors)
        return
      }
      setError(null)
      onClose()
    })
  }

  return data
    ? <form className="main-form" onSubmit={form.handleSubmit(handle)}>
      <div className="form-field" >
        <label htmlFor="user">{session.translates.for("forms.card.fields.userId.title")}:</label>
        <UsernameInput
          fieldName="username"
          formName="card"
          form={form}
          services={services}
          translates={session.translates}
          readOnly={true}
          required={true}
        />
      </div>
      <div className="form-field">
        <label htmlFor="ucid">{session.translates.for("forms.card.fields.ucid.title")}:</label>
        <UCIDInput
          fieldName="ucid"
          formName="card"
          form={form}
          services={services}
          translates={session.translates}
          readOnly={false}
          required={true}
        />
      </div>
      {error && <div className="critic-error-box">{session.translates.for("errors.general_error")}</div>}
      <button className="submit-button" type="submit" disabled={
        !data.username || !data.ucid
      }>
        {session.translates.for("forms.card.submit")}
      </button>
    </form>
    : <div className="spinner"></div>
}

export const ManagerInsertCardPopup = ({ services, session, onClose, username }: ManagerInsertCardPopupProps) => {
  const form = useForm<CardFormData>({
    mode: "onChange",
    defaultValues: {
      ucid: '',
      username
    }
  })
  const [error, setError] = useState<string | null>(null)

  const handle = (data: CardFormData): Promise<void> => {
    if (!data.username) {
      console.error('user is required')
      return Promise.reject()
    }
    if (!data.ucid) {
      console.error('ucid is required')
      return Promise.reject()
    }
    if (!form) return Promise.reject()
    setError(null)
    return services.session.fetchWithToken(`/api/communal/cards/new`, {
      method: 'POST',
      body: JSON.stringify(data)
    }).then(async (r) => {
      if (r.status !== 200) {
        setError('Write data error')
        const errors = await r.json()
        addServerErrors(form, session.translates, errors)
        return
      }
      setError(null)
      form.reset()
      onClose()
    })
  }

  if (!session || !services) {
    return <Spinner />
  }

  return <form className="main-form" onSubmit={form.handleSubmit(handle)}>
    <div className="form-field" >
      <label htmlFor="user">{session.translates.for("forms.card.fields.userId.title")}:</label>
      <UsernameInput
        fieldName="username"
        formName="card"
        form={form}
        services={services}
        translates={session.translates}
        readOnly={true}
        required={true}
      />
    </div>
    <div className="form-field">
      <label htmlFor="ucid">{session.translates.for("forms.card.fields.ucid.title")}:</label>
      <UCIDInput
        fieldName="ucid"
        formName="card"
        form={form}
        services={services}
        translates={session.translates}
        readOnly={false}
        required={true}
      />
    </div>
    {/* <div className="form-field">
      <label htmlFor="label">{session.translates.for("forms.card.fields.label.title")}:</label>
      <input
        type="text"
        id="label"
        {...form.register('label')}
        name="label"
      />
    </div> */}
    {error && <div className="critic-error-box">{session.translates.for("errors.general_error")}</div>}
    <button className="submit-button" type="submit" disabled={!form.formState.isValid}>
      {session.translates.for("forms.card.submit")}
    </button>
  </form>
}

export const CardsMenuPopup = ({ onClose, row }: {
  onClose: () => void
  row: SimpleCard
}) => {
  const { services, session, translates } = useAppContext()
  const navigate = useNavigate()
  const { openModal } = useModal()

  if (!session) {
    return <Spinner />
  }

  let menu: Menu = {
    title: `${row.username}`,
    rows: [
      {
        icon: 'svgfont-edit',
        title: 'elements.table.cards.menu.edit.edit_row',
        action: () => openModal((props) => ManagerEditCardPopup({ ...props, ucid: row.ucid, services, session })),
      },
      {
        icon: 'svgfont-remove',
        title: 'elements.table.cards.menu.edit.remove',
        action: () => openModal((props) => ManagerRemoveCardPopup({ ...props, card: row, services, session })),
      }
    ]
  }

  menu = extendCommunalCardsMenuPopup({
    menu,
    navigate,
    row,
    services,
    session
  })

  return <MenuPopupBody session={session} menu={menu} onClose={onClose} />
}

export const UserManagerCardsPage = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const user = useParams().user
  const searchTableRef = useRef<SearchTableRef>(null)
  const { openModal } = useModal()

  useResponsiveChange()

  const [searchParams] = useSearchParams()
  const searchFromUrl = searchParams.get("search") ?? ""

  const initialPageQuery = useMemo(
    () => ({
      pageNumber: 1,
      pageSize: 30,
      search: searchFromUrl,
    }),
    [searchFromUrl]
  )

  const tableParams: SearchTableParams<SimpleCard> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleCard>> => {
      return services.session.fetchWithToken(`/api/communal/cards/search?user=${user}&${pageQueryParams(q)}`).then(r => {
        if (r.status === 200) return r.json()
        return Promise.reject()
      })
    },
    pageQuery: initialPageQuery,
    toolbox: () => <>
      <div className='tool' onClick={() => openModal((props) => ManagerInsertCardPopup({ ...props, services, session, username: user }))}><i className='svgfont-add' /></div>
    </>,
    defaultSortBy: 'ucid',
    header: (query, onSort) => <>
      <SortableHeader field='ucid' query={query} onSort={onSort}>{session.translates.for('elements.table.cards.ucid')}</SortableHeader>
      <SortableHeader field='username' query={query} onSort={onSort}>{session.translates.for('elements.table.cards.username')}</SortableHeader>
      {/* <div className='hcol'>{session.translates.for('elements.table.cards.label')}</div> */}
      <div className='hcol'></div>
    </>,
    row: (row) => <>
      <div className='col'>{row.ucid}</div>
      <div className='col'>{row.username}</div>
      {/* <div className='col'>{row.label}</div> */}
      <div className='col icocol' onClick={() => openModal((props) => CardsMenuPopup({ ...props, row: row }))}>
        <i className='svgfont-menu' />
      </div>
    </>,
    translates: session.translates
  }

  return <LoggedInPageLayout pageName='communal-cards' services={services} session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}>
    {user ? <h1>{decodeURIComponent(user)}</h1> : null}
    <SearchTable ref={searchTableRef} {...tableParams} />
  </LoggedInPageLayout>
}